import { Input } from '@eika/input';
import { ButtonTypes, StandardButton } from '@eika/button';
import React from 'react';
import { FormikErrors, FormikProps } from 'formik';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ExpandableInput } from '@eika/expandable-input';
import InputLabel from '@eika/input/src/InputLabel';
import { FormValues, Page, PersonalInfoValues } from '@/data/Types';
import FormFieldErrorMessage from '@/components/FormFieldErrorMessage';
import ErrorSubmittingMessage from '@/components/ErrorSubmittingMessage';
import { personalInfoRequiredFields, personalInfoRequiredFieldsMissing } from '@/forms/fields/personalInfo';

const SubmitButton = ({
    submit,
    formik,
    setPage,
}: {
    submit: boolean;
    formik: FormikProps<PersonalInfoValues>;
    setPage: (page: Page) => void;
}) =>
    submit ? (
        <ButtonWithSpinner
            className="form-submit-button-with-spinner"
            showSpinner={formik.isSubmitting}
            buttonType={ButtonTypes.STANDARD_POSITIVE}
            type="submit"
        >
            Send inn
        </ButtonWithSpinner>
    ) : (
        <StandardButton
            className="form-submit-button-with-spinner"
            onClick={async () => {
                await formik.setTouched(personalInfoRequiredFields());
                const errorFields = await formik
                    .validateForm()
                    .then((errors: FormikErrors<FormValues>) => Object.keys(errors));

                if (personalInfoRequiredFieldsMissing(errorFields)) return;

                setPage('insurances');
            }}
        >
            Neste
        </StandardButton>
    );

const PersonalInfo = ({
    formik,
    setPage,
    sentError,
    withOptionalComment,
    shortenWizard = false,
}: {
    formik: FormikProps<PersonalInfoValues>;
    setPage: (page: Page) => void;
    sentError: boolean;
    withOptionalComment: boolean;
    shortenWizard: boolean;
}) => {
    return (
        <>
            <h1 className="form-title">Meld interesse til Fremtind</h1>
            <p className="form-description">
                Legg igjen kontaktinformasjon{!shortenWizard && ', og fortell oss kort hva du trenger,'} så tar vi
                kontakt med deg!
            </p>
            <div className="form-fields">
                <div className="form-field">
                    <Input
                        label="Navn"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <FormFieldErrorMessage formik={formik} field="name" />
                </div>
                <div className="form-field__short">
                    <Input
                        label="Telefon"
                        name="telephone"
                        type="tel"
                        value={formik.values.telephone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <FormFieldErrorMessage formik={formik} field="telephone" />
                </div>
                <div className="form-field">
                    <Input
                        label="E-post"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <FormFieldErrorMessage formik={formik} field="email" />
                </div>
                <div className="form-field__short">
                    <Input
                        label="Fødselsdato eller organisasjons&shy;nummer"
                        name="foedselsdatoEllerOrgnr"
                        value={formik.values.foedselsdatoEllerOrgnr}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <FormFieldErrorMessage formik={formik} field="foedselsdatoEllerOrgnr" />
                </div>
                {withOptionalComment && (
                    <div className="form-field__long form-field__optional">
                        <InputLabel label="Eventuell kommentar">
                            <ExpandableInput
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                minRows={3}
                                id="comment"
                            />
                        </InputLabel>
                    </div>
                )}
            </div>
            {sentError && <ErrorSubmittingMessage />}
            <SubmitButton formik={formik} submit={shortenWizard} setPage={setPage} />
        </>
    );
};

export default PersonalInfo;
